import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router'
import { Typography, Alert, Box } from '@mui/material'

import useTranslations from '../../i18n/useTranslations'
import { useUserContext } from '../../auth/UserProvider'
import { useDestinationCountries } from '../../services/destinationCountries'
import {
  useShippingsTracking,
  useShippingStates,
} from '../../services/shippingsTracking'
import ShippingsTrackingView from './View'
import SearchInput from './SearchInput'
import { FiltersDrawerView } from './FiltersDrawer'
import FilteringChips from './FilteringChips'
import DrawerWrapper from '../../ui/modules/DrawerWrapper'
import TrackingTable from './TrackingTable'
import EmptyStateWrapper from '../../ui/components/EmptyState/PaperWrapper'

import useQueryParamsFilters from './useQueryParamsFilters'
import {
  TrackingTypeSwitcherView,
  TRACKING_OPTIONS,
} from './TrackingTypeSwitcher'
import {
  SHIPPINGS_STATIC_DEFAULT_FILTERS,
  SHIPPING_STATUS_CODES,
} from '../../common/constants'
import StatusCodeQuickFilters from './StatusCodeQuickFilters'
import { ReactComponent as MapPin } from '../../assets/icons/map-pin.svg'
import Truck from '../../assets/animations/truck'

const ShippingsTracking = () => {
  const history = useHistory()
  const { statusList, auditData, centres, isLoading } = useUserContext()
  const user = useUserContext()
  const { data: countries } = useDestinationCountries()
  const t = useTranslations()

  const [selectedTracking, setSelectedTracking] = useState(
    TRACKING_OPTIONS.shippings,
  )
  const enabledFilterByStatusCode =
    selectedTracking !== TRACKING_OPTIONS.returns &&
    selectedTracking !== TRACKING_OPTIONS.unscheduledPickups
  const enabledGenerateManifest =
    selectedTracking !== TRACKING_OPTIONS.returns &&
    selectedTracking !== TRACKING_OPTIONS.unscheduledPickups

  const trackingTableRef = useRef(null)
  const [, setBlobProvider] = useState(null)
  const [numberSelected, setNumberSelected] = useState(null)
  const [open, setOpen] = useState(null)

  const handleGenerateManifestClick = () => {
    const blobProvider = trackingTableRef.current.onGenerateManifest()
    setBlobProvider(blobProvider)
  }

  const {
    filters,
    handleReplaceFilter,
    handleUpdatePrevFilters,
    handleUpdateDefaultFilters,
    handleClearFilters,
  } = useQueryParamsFilters({
    centres,
  })

  const initialState = {
    clientCenterCode: undefined,
    clientCode: undefined,
    country: undefined,
    destinPostalCode: undefined,
    incidentCode: undefined,
    originPostalCode: undefined,
    postalCodeFrom: undefined,
    postalCodeTo: undefined,
    search: undefined,
    serviceType: undefined,
    statusCode: undefined,
  }

  const hasChangedFromUndefined = (initial, current) => {
    const hasChanged = Object.entries(initial).some(([key, value]) => {
      return (
        (value === undefined || value === null) &&
        current[key] !== undefined &&
        current[key] !== null
      )
    })
    const today = new Date()
    const dateTo = new Date(current.dateTo)
    const dateFrom = new Date(current.dateFrom)

    const threeMonthsAgo = new Date()
    threeMonthsAgo.setMonth(today.getMonth() - 3)
    const isDateRangeIncorrect =
      dateTo.toDateString() !== today.toDateString() &&
      dateFrom.toDateString() !== threeMonthsAgo.toDateString()

    return hasChanged || isDateRangeIncorrect
  }

  const { isLoading: isLoadingShippings, data: shippingsData } =
    useShippingsTracking({
      type: 'shippings',
      filters: {
        // NOTE: el hook `useShippingsTracking` espera recibir en estos filtros
        // una propiedad `page` y otra `rowsPerPage`. Antes de tener que incluir
        // las incidencias, el hook `useQueryParamsFilters` devolvía estos dos
        // propiedades tal cual. Ahora este hook (`useQueryParamsFilters`)
        // distingue la paginación de los envíos y de las incidencias
        // (`shippingsPage`, `shippingsRowsPerPage`, `incidentsPage` y
        // `incidentsRowsPerPage`). Estas dos últimas propiedades todavía no se
        // han incluido.
        ...filters,
        page: filters.shippingsPage,
        rowsPerPage: filters.shippingsRowsPerPage,
        sortColumn: filters.shippingsSortColumn,
        sortType: filters.shippingsSortType,
      },
      statusList,
    })

  const { isLoading: isLoadingIncidents, data: incidentsData } =
    useShippingsTracking({
      type: 'incidents',
      filters: {
        ...filters,
        page: filters.incidentsPage,
        rowsPerPage: filters.incidentsRowsPerPage,
        sortColumn: filters.incidentsSortColumn,
        sortType: filters.incidentsSortType,
        hasIncident: true,
      },
      statusList,
      allowCancelled: true,
    })

  const { isLoading: isLoadingReturns, data: returnsData } =
    useShippingsTracking({
      type: 'returns',
      filters: {
        ...filters,
        statusCode: [SHIPPING_STATUS_CODES.returned],
        page: filters.returnsPage,
        rowsPerPage: filters.returnsRowsPerPage,
        sortColumn: filters.returnsSortColumn,
        sortType: filters.returnsSortType,
      },
      statusList,
      allowCancelled: true,
    })

  const {
    isLoading: isLoadingUnscheduledPickups,
    data: unscheduledPickupsData,
  } = useShippingsTracking({
    type: 'unscheduledPickups',
    filters: {
      ...filters,
      statusCode: [SHIPPING_STATUS_CODES.manifested],
      sortColumn: SHIPPINGS_STATIC_DEFAULT_FILTERS.unscheduledPickupSortType,
      sortType: SHIPPINGS_STATIC_DEFAULT_FILTERS.unscheduledPickupSortColumn,
      rowsPerPage:
        SHIPPINGS_STATIC_DEFAULT_FILTERS.unscheduledPickupRowsPerPage,
      isUnscheduledPickupSearch: true,
    },
    statusList,
    allowCancelled: true,
  })

  const shippingCodes =
    shippingsData?.shippings?.map(item => item.info.shippingCode) || []
  const incidentsCodes =
    incidentsData?.shippings?.map(item => item.info.shippingCode) || []
  const returnCodes =
    returnsData?.shippings?.map(item => item.info.shippingCode) || []
  const unscheduledPickupCodes =
    unscheduledPickupsData?.shippings?.map(item => item.info.shippingCode) || []

  const { data: shippingsStates, isLoading: isLoadingShippingsStates } =
    useShippingStates(shippingCodes)

  const { data: incidentsStates, isLoading: isLoadingIncidentsStates } =
    useShippingStates(incidentsCodes)

  const { data: returnStates, isLoading: isLoadingReturnStates } =
    useShippingStates(returnCodes)

  const {
    data: unscheduledPickupStates,
    isLoading: isLoadingUnscheduledPickupStates,
  } = useShippingStates(unscheduledPickupCodes)

  useEffect(() => {
    if (!history.location.search) {
      handleClearFilters()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.search, selectedTracking])

  useEffect(() => {
    if (hasChangedFromUndefined(initialState, filters)) {
      setNumberSelected(null)
    }
  }, [filters])

  useEffect(() => {
    handleClearFilters()
  }, [selectedTracking])

  const [incidentBeingSolved, setIncidentBeingSolved] = useState(null)
  const isIncidentDrawerOpen = Boolean(incidentBeingSolved)
  const handleCloseIncidentWidget = () => setIncidentBeingSolved(null)

  useEffect(() => {
    document.addEventListener(
      'incident-solver:general:close',
      handleCloseIncidentWidget,
    )

    return () => {
      document.removeEventListener(
        'incident-solver:general:close',
        handleCloseIncidentWidget,
      )
    }
  }, [])

  const groupUnscheduledPickupsShippingsBySenderAddress = shippings => {
    if (shippings === undefined || shippings.length === 0) {
      return [{ header: '', shippings: [] }]
    }

    const groupBySenderAddress = shipping =>
      `${shipping.info.senderAddress}, ${shipping.info.originPostalCode}, ${shipping.info.senderAddress2}`

    const groups = Object.groupBy(shippings || [], groupBySenderAddress)
    return Object.keys(groups).map(key => ({
      header: key,
      shippings: groups[key],
    }))
  }

  return (
    <>
      <ShippingsTrackingView
        isLoading={
          isLoadingShippings ||
          isLoadingIncidents ||
          isLoadingReturns ||
          isLoadingUnscheduledPickups ||
          isLoadingShippingsStates ||
          isLoadingIncidentsStates ||
          isLoadingReturnStates ||
          isLoadingUnscheduledPickupStates
        }
        searchSlot={
          <SearchInput
            queryValue={filters.search}
            search={value => {
              setSelectedTracking(TRACKING_OPTIONS.shippings) // Añade esta línea
              handleReplaceFilter({ searchType: 'search', value })
            }}
            clearSearch={() => {
              handleReplaceFilter({ searchType: 'search', value: undefined })
            }}
          />
        }
        advancedFiltersSlot={
          <FiltersDrawerView
            onUpdateFilters={handleUpdateDefaultFilters}
            user={user}
            isOpen={open}
            setOpen={setOpen}
            countries={countries}
            filters={filters}
            disabledFilterByStatusCode={!enabledFilterByStatusCode}
          />
        }
      >
        {shippingsData?.shippings?.length === 0 ? (
          <Alert
            severity="warning"
            sx={{
              minWidth: 'max-content',
            }}
          >
            <Typography component="p" variant="body2">
              {t('alert:shippings-search:no-shippings')}
            </Typography>
          </Alert>
        ) : null}
        
        {numberSelected?.length ? (
          <Box
            sx={{
              backgroundColor: '#F3F5F5',
              padding: 2,
              marginY: 3,
              color: '#666666',
              justifyContent: 'center',
              display: 'flex',
            }}
          >

            <b>
              {t('shipping-tracking:shipping-selected', {
                shippingCount: numberSelected?.length,
                text: (
                  <b
                    onClick={() => {
                      setOpen(!open)
                    }}
                    style={{
                      color: '#00BEB4',
                      cursor: 'pointer',
                    }}
                  >
                    {hasChangedFromUndefined(initialState, filters)
                      ? t('shipping-tracking:shipping-selected-3')
                      : t('shipping-tracking:shipping-selected-2')}
                  </b>
                ),
              })}
            </b>
          </Box>
        ) : null}

        <TrackingTypeSwitcherView
          changeSelectedTracking={setSelectedTracking}
          selectedTracking={selectedTracking}
          incidentsCount={incidentsData?.pagination.totalRows}
          unscheduledPickupsCount={unscheduledPickupsData?.shippings?.length}
          statusCodeSelectorSlot={
            <StatusCodeQuickFilters
              filters={filters}
              openFilters={()=>setOpen(true)}
              selectedTracking={selectedTracking}
              updateFilters={activeStatuses => {
                handleReplaceFilter({
                  searchType:
                    selectedTracking === 'incidents'
                      ? 'incidentCode'
                      : 'statusCode',
                  value: activeStatuses,
                })
              }}
              disabledFilterByStatusCode={!enabledFilterByStatusCode}
              disabledGenerateManifest={!enabledGenerateManifest}
              onGenerateManifestClick={handleGenerateManifestClick}
            />
          }
        >
          {selectedTracking !== TRACKING_OPTIONS.unscheduledPickups && (
            <FilteringChips
              filters={filters}
              disabledStatusCodeFilters={!enabledFilterByStatusCode}
              onUpdateFilters={handleUpdateDefaultFilters}
              onClearFilters={handleClearFilters}
            />
          )}

          {selectedTracking === TRACKING_OPTIONS.shippings && (
            <Shippings
              data={shippingsData?.shippings ?? []}
              states={shippingsStates}
              isLoading={
                isLoading ||
                isLoadingShippings ||
                isLoadingIncidents ||
                isLoadingShippingsStates ||
                isLoadingIncidentsStates
              }
              filters={filters}
              updatePrevFilters={handleUpdatePrevFilters}
              setIncidentBeingSolved={setIncidentBeingSolved}
              tableRef={trackingTableRef}
              noResultsTitle={t('message:no-results')}
              noResultsSubtitle={t('message:no-results:shippings')}
              noDataTitle={t('empty:no-shippings-title')}
              noDataSubtitle={t('empty:no-shippings')}
              showPagination
              tableParams={{
                page: Number(filters.shippingsPage),
                rowsPerPage: Number(filters.shippingsRowsPerPage),
                sortType: filters.shippingsSortType,
                sortColumn: filters.shippingsSortColumn,
                totalRows: Number(shippingsData?.pagination.totalRows),
              }}
              changeTableParams={params => {
                handleUpdatePrevFilters({
                  shippingsPage: params.page,
                  shippingsRowsPerPage: params.rowsPerPage,
                  shippingsSortColumn: params.sortColumn,
                  shippingsSortType: params.sortType,
                })
              }}
              changeSelectedTracking={setSelectedTracking}
              setNumberSelected={setNumberSelected}
              numberSelected={numberSelected}
            />
          )}
          {selectedTracking === TRACKING_OPTIONS.incidents && (
            <Shippings
              data={incidentsData?.shippings ?? []}
              states={incidentsStates}
              filters={filters}
              isLoading={
                isLoadingShippings ||
                isLoadingIncidents ||
                isLoadingShippingsStates ||
                isLoadingIncidentsStates
              }
              updatePrevFilters={handleUpdatePrevFilters}
              setIncidentBeingSolved={setIncidentBeingSolved}
              tableRef={trackingTableRef}
              pagination={incidentsData?.pagination}
              noResultsTitle={t('message:no-results')}
              noResultsSubtitle={t('message:no-results:incidents')}
              noDataTitle={t('empty:everything-goes-smoothly')}
              noDataSubtitle={t('empty:no-incidents')}
              showPagination
              tableParams={{
                page: Number(filters.incidentsPage),
                rowsPerPage: Number(filters.incidentsRowsPerPage),
                sortType: filters.incidentsSortType,
                sortColumn: filters.incidentsSortColumn,
                totalRows: Number(incidentsData?.pagination.totalRows),
              }}
              changeTableParams={params => {
                handleUpdatePrevFilters({
                  incidentsPage: params.page,
                  incidentsRowsPerPage: params.rowsPerPage,
                  incidentsSortColumn: params.sortColumn,
                  incidentsSortType: params.sortType,
                })
              }}
              changeSelectedTracking={setSelectedTracking}
              hasTruck={true}
              setNumberSelected={setNumberSelected}
              numberSelected={numberSelected}
            />
          )}
          {selectedTracking === TRACKING_OPTIONS.returns && (
            <Shippings
              data={returnsData?.shippings ?? []}
              states={returnStates}
              filters={filters}
              isLoading={isLoadingReturns}
              updatePrevFilters={handleUpdatePrevFilters}
              setIncidentBeingSolved={setIncidentBeingSolved}
              tableRef={trackingTableRef}
              noResultsTitle={t('message:no-results')}
              noResultsSubtitle={t('message:no-results:returns')}
              noDataTitle={t('empty:no-returns-title')}
              noDataSubtitle={t('empty:no-returns-subtitle')}
              showPagination
              tableParams={{
                page: Number(filters.returnsPage),
                rowsPerPage: Number(filters.returnsRowsPerPage),
                sortType: filters.returnsSortType,
                sortColumn: filters.returnsSortColumn,
                totalRows: Number(returnsData?.pagination.totalRows),
              }}
              changeTableParams={params => {
                handleUpdatePrevFilters({
                  returnsPage: params.page,
                  returnsRowsPerPage: params.rowsPerPage,
                  returnsSortColumn: params.sortColumn,
                  returnsSortType: params.sortType,
                })
              }}
              setNumberSelected={setNumberSelected}
              numberSelected={numberSelected}
            />
          )}
          {selectedTracking === TRACKING_OPTIONS.unscheduledPickups &&
            groupUnscheduledPickupsShippingsBySenderAddress(
              unscheduledPickupsData?.shippings,
            ).map(shippingsGroup => (
              <Shippings
                key={shippingsGroup.header}
                data={shippingsGroup.shippings}
                states={unscheduledPickupStates}
                filters={filters}
                isLoading={
                  isLoadingUnscheduledPickups ||
                  isLoadingUnscheduledPickupStates
                }
                updatePrevFilters={handleUpdatePrevFilters}
                setIncidentBeingSolved={setIncidentBeingSolved}
                tableRef={trackingTableRef}
                noResultsTitle={t('message:no-results')}
                noResultsSubtitle={t('message:no-results:unscheduled')}
                noDataTitle={t('empty:everything-goes-smoothly')}
                noDataSubtitle={t('empty:no-unscheduled-subtitle')}
                showPagination={false}
                tableParams={{
                  page: Number(filters.returnsPage),
                  rowsPerPage: Number(filters.returnsRowsPerPage),
                  sortType: filters.returnsSortType,
                  sortColumn: filters.returnsSortColumn,
                  totalRows: Number(returnsData?.pagination.totalRows),
                }}
                changeTableParams={params => {
                  handleUpdatePrevFilters({
                    returnsPage: params.page,
                    returnsRowsPerPage: params.rowsPerPage,
                    returnsSortColumn: params.sortColumn,
                    returnsSortType: params.sortType,
                  })
                }}
                collapsableHeader={{
                  title: t('unscheduled-pickup:table-header-title'),
                  subtitle: shippingsGroup.header,
                  icon: <MapPin />,
                }}
                shippingsType={TRACKING_OPTIONS.unscheduledPickups}
                hasTruck={true}
                setNumberSelected={setNumberSelected}
                numberSelected={numberSelected}
              />
            ))}
        </TrackingTypeSwitcherView>
      </ShippingsTrackingView>
      <DrawerWrapper
        isOpen={isIncidentDrawerOpen}
        onClose={handleCloseIncidentWidget}
      >
        <incident-solver
          incident-type-code={incidentBeingSolved?.incidentTypeCode}
          shipping-code={incidentBeingSolved?.shippingCode}
          process-code={auditData.process_code}
          user-code={auditData.user_code}
        />
      </DrawerWrapper>
    </>
  )
}

function Shippings({
  data,
  states,
  filters,
  noResultsTitle,
  noResultsSubtitle,
  tableParams,
  showPagination,
  noDataTitle,
  noDataSubtitle,
  changeTableParams,
  setIncidentBeingSolved,
  tableRef,
  changeSelectedTracking,
  isLoading,
  collapsableHeader,
  shippingsType,
  hasTruck,
  setNumberSelected,
  numberSelected,
}) {
  if (Boolean(filters.search) && data.length === 0 && !isLoading) {
    return <EmptyState title={noDataTitle} subtitle={noDataSubtitle} />
  }

  if (!Boolean(filters.search) && data.length === 0 && !isLoading) {
    return (
      <Box>
        {hasTruck && <Truck size={300}></Truck>}
        <EmptyState
          title={noDataTitle}
          subtitle={noDataSubtitle}
          withImage={hasTruck}
        />
      </Box>
    )
  }

  return (
    <TrackingTable
      shippings={data}
      states={states}
      isLoading={isLoading}
      tableParams={tableParams}
      showPagination={showPagination}
      collapsableHeader={collapsableHeader}
      shippingsType={shippingsType}
      onChangeTableParams={changeTableParams}
      onClickResolveIncident={setIncidentBeingSolved}
      changeSelectedTracking={changeSelectedTracking}
      ref={tableRef}
      numberSelected={numberSelected}
      setNumberSelected={setNumberSelected}
    />
  )
}

Shippings.propTypes = {
  data: PropTypes.array,
  states: PropTypes.array,
  filters: PropTypes.object,
  noResultsTitle: PropTypes.string,
  noResultsSubtitle: PropTypes.string,
  tableParams: PropTypes.object,
  showPagination: PropTypes.bool,
  noDataTitle: PropTypes.string,
  noDataSubtitle: PropTypes.string,
  changeTableParams: PropTypes.func,
  setIncidentBeingSolved: PropTypes.func,
  tableRef: PropTypes.any,
  changeSelectedTracking: PropTypes.func,
  isLoading: PropTypes.bool,
  collapsableHeader: PropTypes.object,
  shippingsType: PropTypes.string,
  hasTruck: PropTypes.bool,
}

function EmptyState({ title, subtitle, withImage }) {
  return (
    <EmptyStateWrapper withImage={withImage}>
      <Typography variant="h2" className={'decorator'}>
        {title}
      </Typography>
      <Typography sx={{ color: 'neutral.main' }}>{subtitle}</Typography>
    </EmptyStateWrapper>
  )
}

EmptyState.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  withImage: PropTypes.bool,
}

export default ShippingsTracking
